// @ts-check

/**
 * @file Provides the project board task action menu.
 */
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import ActionMenu from "../../../components/action-menu"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/**
 * @typedef {object} ProjectBoardTaskMenuProps
 * @property {boolean} isVisible Whether the menu is visible.
 * @property {(...args: unknown[]) => unknown} onClosePress Close press handler.
 * @property {(...args: unknown[]) => unknown} onDeletePress Delete press handler.
 * @property {(...args: unknown[]) => unknown} onEditPress Edit press handler.
 * @property {string} [testID] Stable test id.
 */

/** @typedef {Record<string, never>} ProjectBoardTaskMenuState */

/**
 * Project board task action menu.
 * @augments {ShapeComponent<ProjectBoardTaskMenuProps, ProjectBoardTaskMenuState>}
 */
class ProjectBoardTaskMenu extends ShapeComponent {
  static propTypes = PropTypesExact({
    isVisible: PropTypes.bool.isRequired,
    onClosePress: PropTypes.func.isRequired,
    onDeletePress: PropTypes.func.isRequired,
    onEditPress: PropTypes.func.isRequired,
    testID: PropTypes.string
  })
  static defaultProps = {
    testID: undefined
  }

  /** Initializes locale hooks for translated labels. */
  setup() {
    useLocale()
  }

  /**
   * Renders the task action menu.
   * @returns {ReactNode} Task action menu content.
   */
  render() {
    const testID = this.p.testID || "projectBoardTaskMenu"
    return (
      <ActionMenu
        isVisible={this.p.isVisible}
        items={[
          {
            iconName: "pen-to-square",
            label: _("Edit task"),
            onPress: this.p.onEditPress,
            testID: `${testID}/editButton`
          },
          {
            destructive: true,
            iconName: "trash",
            label: _("Delete Task"),
            onPress: this.p.onDeletePress,
            testID: `${testID}/deleteButton`
          }
        ]}
        onClosePress={this.p.onClosePress}
        testID={testID}
        title={_("Task actions")}
      />
    )
  }
}

/** @type {NamedExoticComponent<ProjectBoardTaskMenuProps>} */
const ProjectBoardTaskMenuComponent = memo(shapeComponent(ProjectBoardTaskMenu))

export default ProjectBoardTaskMenuComponent
